import { useState } from "react";
import { createPortal } from "react-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type {
  DeploymentDTO,
  EnvironmentDeploymentsDTO,
  ProjectDetailDTO,
  ReleaseStatus,
} from "@app-factory/shared";
import { api } from "@/lib/api";
import { cs } from "@/i18n/cs";
import { Btn, Chip, EmptyState, Field, Panel, inputClass, type Tone } from "@/design/primitives";
import { relativeTime } from "@/lib/format";
import { canManage, useProjectRole } from "../project-data";

const statusTone: Record<ReleaseStatus, Tone> = { DRAFT: "dim", IN_PROGRESS: "amber", RELEASED: "green" };

/** Stavy z Railway/Coolify (+ RUNNING/PENDING ze zeaburovské historie) — neuzavřený výčet, neznámé padají do "dim". */
function deployTone(status: string): Tone {
  if (status === "SUCCESS" || status === "RUNNING") return "green";
  if (
    ["QUEUED", "WAITING", "INITIALIZING", "NEEDS_APPROVAL", "BUILDING", "DEPLOYING", "PENDING"].includes(status)
  )
    return "amber";
  if (status === "FAILED" || status === "CRASHED") return "red";
  return "dim";
}

function DeploymentRow({ d, onLog }: { d: DeploymentDTO; onLog: (d: DeploymentDTO) => void }) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1 text-[11px]" data-testid="deployment-row">
      <span className="flex min-w-0 items-center gap-2">
        <Chip tone={deployTone(d.status)}>{d.status}</Chip>
        <span className="shrink-0" style={{ color: "var(--text-primary)" }}>
          {d.codebaseSlug ?? "service"}
        </span>
        {d.commitSha ? (
          <span className="truncate" style={{ color: "var(--text-primary)" }}>
            {d.commitSha.slice(0, 7)}
            {d.commitMessage ? ` · ${d.commitMessage}` : ""}
          </span>
        ) : null}
      </span>
      <span className="flex shrink-0 items-center gap-2">
        <span className="text-[11px]" style={{ color: "var(--text-tertiary)" }}>
          {d.deployedAt ? relativeTime(d.deployedAt) : ""}
        </span>
        {d.hostingUrl ? (
          <a
            href={d.hostingUrl}
            target="_blank"
            rel="noreferrer"
            className="text-[11px] tracking-[0.08em] underline"
            style={{ color: "var(--text-secondary)" }}
            data-testid="deployment-hosting-link"
          >
            {cs.releases.deployments.hostingLink} ↗
          </a>
        ) : null}
        {d.url ? (
          <a
            href={d.url}
            target="_blank"
            rel="noreferrer"
            className="text-[11px] tracking-[0.08em] underline"
            style={{ color: "var(--accent)" }}
            data-testid="deployment-open"
          >
            {cs.releases.deployments.open} ↗
          </a>
        ) : null}
        <Btn onClick={() => onLog(d)}>{cs.releases.deployments.buildLog}</Btn>
      </span>
    </div>
  );
}

function BuildLogModal({ deployment, onClose }: { deployment: DeploymentDTO; onClose: () => void }) {
  const log = useQuery({
    queryKey: ["deployment-log", deployment.id],
    queryFn: () => api.deploymentLogs(deployment.id),
  });
  // portál do <body>: obsah stránky je v `af-animate-fade`, což je vlastní
  // vrstva — modál uvnitř by se nedostal nad rail ani nad sticky prvky
  return createPortal(
<div
      className="fixed inset-0 z-50 flex items-center justify-center p-3 md:p-6"
      style={{ background: "var(--surface-overlay)", backdropFilter: "blur(3px)" }}
      onClick={onClose}
      data-testid="build-log-modal"
    >
      <Panel brackets className="max-h-[85vh] w-full max-w-[820px] overflow-hidden p-4 md:p-5" onClick={(e) => e.stopPropagation()}>
        <div className="mb-2 flex items-center justify-between">
          <span className="af-label" style={{ color: "var(--accent)" }}>
            {cs.releases.deployments.buildLog} · {deployment.codebaseSlug ?? deployment.hostingDeploymentId.slice(0, 8)}
            {deployment.commitSha ? ` · ${deployment.commitSha.slice(0, 7)}` : ""}
          </span>
          <button type="button" className="cursor-pointer text-[11px]" style={{ color: "var(--text-secondary)" }} onClick={onClose}>
            ✕
          </button>
        </div>
        <pre
          className="max-h-[70vh] overflow-auto whitespace-pre-wrap break-words text-[11px] leading-relaxed"
          style={{ color: "var(--text-primary)" }}
        >
          {log.isError
            ? cs.releases.deployments.logError
            : log.data
              ? log.data.logs || cs.releases.deployments.logEmpty
              : "…"}
        </pre>
      </Panel>
    </div>,
    document.body,
  );
}

function EnvironmentPanel({ env, onLog }: { env: EnvironmentDeploymentsDTO; onLog: (d: DeploymentDTO) => void }) {
  const older = env.history.filter((h) => !env.current.some((c) => c.id === h.id));
  return (
    <Panel className="p-4" data-testid="environment-deployments">
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-3">
          <span className="af-display text-[12px] font-semibold" style={{ color: "var(--text-primary)" }}>
            {env.environment.name}
          </span>
          <Chip tone={env.environment.kind === "PRODUCTION" ? "violet" : "cyan"}>{env.environment.kind}</Chip>
          {env.environment.hostingProvider ? (
            <Chip tone="dim">
              {cs.releases.deployments.provider[env.environment.hostingProvider] ?? env.environment.hostingProvider}
            </Chip>
          ) : null}
          {env.environment.branch ? (
            <span className="text-[11px]" style={{ color: "var(--text-secondary)" }}>
              {cs.releases.deployments.branch(env.environment.branch)}
            </span>
          ) : null}
        </div>
        {env.environment.url ? (
          <a
            href={env.environment.url}
            target="_blank"
            rel="noreferrer"
            className="text-[11px] tracking-[0.08em] underline"
            style={{ color: "var(--accent)" }}
          >
            {cs.releases.deployments.open} ↗
          </a>
        ) : null}
      </div>
      {env.environment.hostingProvider === "ZEABUR" ? (
        <div className="text-[11px]" style={{ color: "var(--text-tertiary)" }} data-testid="deployments-zeabur">
          {cs.releases.deployments.zeaburPending}
        </div>
      ) : null}
      {!env.environment.hostingProjectId ? (
        <div className="text-[11px]" style={{ color: "var(--text-tertiary)" }}>
          {cs.releases.deployments.empty}
        </div>
      ) : env.current.length === 0 ? (
        <div className="text-[11px]" style={{ color: "var(--text-tertiary)" }}>
          {cs.releases.deployments.noBuilds}
        </div>
      ) : (
        <div className="flex flex-col gap-1.5">
          {env.current.map((d) => (
            <DeploymentRow key={d.id} d={d} onLog={onLog} />
          ))}
        </div>
      )}
      {older.length > 0 ? (
        <details className="mt-2.5 pt-2" style={{ borderTop: "1px solid var(--border-subtle)" }}>
          <summary className="af-label-dim cursor-pointer text-[11px]">
            {cs.releases.deployments.history} ({older.length})
          </summary>
          <div className="mt-1.5 flex flex-col gap-1.5">
            {older.map((d) => (
              <DeploymentRow key={d.id} d={d} onLog={onLog} />
            ))}
          </div>
        </details>
      ) : null}
    </Panel>
  );
}

/** NASAZENÍ: co kde běží (Coolify/Railway) — plněno pollerem, mapping hlásí worker. */
function DeploymentsSection({ project }: { project: ProjectDetailDTO }) {
  const [logOf, setLogOf] = useState<DeploymentDTO | null>(null);
  const deployments = useQuery({
    queryKey: ["section", project.id, "releases", "deployments"],
    queryFn: () => api.deployments(project.id),
    refetchInterval: 30_000,
  });
  const list = deployments.data?.environments ?? [];
  if (list.length === 0) return null;
  return (
    <div className="mb-5">
      <div className="mb-3">
        <span className="af-label-dim">{cs.releases.deployments.header}</span>
      </div>
      <div className="flex max-w-[860px] flex-col gap-3">
        {list.map((env) => (
          <EnvironmentPanel key={env.environment.id} env={env} onLog={setLogOf} />
        ))}
      </div>
      {logOf ? <BuildLogModal deployment={logOf} onClose={() => setLogOf(null)} /> : null}
    </div>
  );
}

/** RELEASES TAB dle /Design: karty releases s obsahem a stavem. */
export function ReleasesTab({ project }: { project: ProjectDetailDTO }) {
  const queryClient = useQueryClient();
  const role = useProjectRole(project);
  const manager = canManage(role);
  const releases = useQuery({
    queryKey: ["section", project.id, "releases"],
    queryFn: () => api.releases(project.id),
  });
  const [creating, setCreating] = useState(false);
  const [version, setVersion] = useState("");
  const [name, setName] = useState("");
  const [error, setError] = useState<string | null>(null);

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: ["section", project.id, "releases"] });

  const createMutation = useMutation({
    mutationFn: () => api.createRelease(project.id, { version, name, notes: "", status: "DRAFT" }),
    onSuccess: () => {
      setCreating(false);
      setVersion("");
      setName("");
      void invalidate();
    },
    onError: (err) => setError(err instanceof Error ? err.message : cs.common.error),
  });
  const statusMutation = useMutation({
    mutationFn: (input: { id: string; status: ReleaseStatus }) =>
      api.updateRelease(input.id, { status: input.status }),
    onSuccess: invalidate,
  });

  const list = releases.data?.releases ?? [];

  return (
    <div className="mt-4">
      <DeploymentsSection project={project} />
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <span className="af-label-dim">{cs.releases.header}</span>
        {manager ? (
          <Btn onClick={() => setCreating(true)} data-testid="release-new">
            {cs.releases.newRelease}
          </Btn>
        ) : null}
      </div>

      {creating ? (
        <Panel brackets className="mb-3 max-w-xl p-4">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-[140px_1fr]">
            <Field label={cs.fields.version}>
              <input className={inputClass} value={version} onChange={(e) => setVersion(e.target.value)} placeholder="v1.0" autoFocus />
            </Field>
            <Field label={cs.fields.nameOptional}>
              <input className={inputClass} value={name} onChange={(e) => setName(e.target.value)} />
            </Field>
          </div>
          {error ? (
            <div className="mt-2 text-[11px]" style={{ color: "var(--critical)" }}>
              ⚠ {error}
            </div>
          ) : null}
          <div className="mt-3 flex justify-end gap-2">
            <Btn onClick={() => setCreating(false)}>{cs.common.cancel}</Btn>
            <Btn variant="primary" disabled={!version.trim() || createMutation.isPending} onClick={() => createMutation.mutate()}>
              {cs.common.create}
            </Btn>
          </div>
        </Panel>
      ) : null}

      <div className="flex max-w-[860px] flex-col gap-3">
        {list.map((r) => (
          <Panel key={r.id} tone={r.status === "IN_PROGRESS" ? "amber" : undefined} className="p-4" data-testid="release-card">
            <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
              <div className="flex flex-wrap items-center gap-3">
                <span className="af-card-title" style={{ color: "var(--text-primary)" }}>
                  {r.version}
                </span>
                <Chip tone={statusTone[r.status]}>{r.status}</Chip>
                {r.name ? (
                  <span className="text-[12px]" style={{ color: "var(--text-secondary)" }}>
                    {r.name}
                  </span>
                ) : null}
              </div>
              <span className="text-[11px]" style={{ color: "var(--text-tertiary)" }}>
                {r.releasedAt ? relativeTime(r.releasedAt) : ""}
              </span>
            </div>
            <div className="text-[12px]" style={{ color: "var(--text-primary)" }}>
              {r.notes || cs.releases.tasksDone(r.doneTaskCount, r.taskCount)}
            </div>
            {r.tasks.length > 0 ? (
              <div className="mt-2.5 flex flex-col gap-1 pt-2" style={{ borderTop: "1px solid var(--border-subtle)" }} data-testid="release-tasks">
                {r.tasks.map((t) => (
                  <div key={t.id} className="flex items-center justify-between gap-2 text-[11px]">
                    <span className="min-w-0 break-words" style={{ color: "var(--text-primary)" }}>
                      #{t.number} {t.title}
                    </span>
                    <span className="flex shrink-0 items-center gap-2 text-[11px] tracking-[0.08em]">
                      {t.qaVerdict ? (
                        <span style={{ color: t.qaVerdict === "PASSED" ? "var(--positive)" : "var(--critical)" }}>
                          QA {t.qaVerdict === "PASSED" ? "✓" : "✗"}
                        </span>
                      ) : null}
                      <span style={{ color: t.status === "DONE" ? "var(--positive)" : "var(--text-secondary)" }}>
                        {t.status}
                      </span>
                    </span>
                  </div>
                ))}
              </div>
            ) : null}
            {manager && r.status !== "RELEASED" ? (
              <div className="mt-2.5 flex flex-wrap gap-2">
                {r.status === "DRAFT" ? (
                  <Btn onClick={() => statusMutation.mutate({ id: r.id, status: "IN_PROGRESS" })}>
                    START
                  </Btn>
                ) : null}
                <Btn tone="green" onClick={() => statusMutation.mutate({ id: r.id, status: "RELEASED" })}>
                  OZNAČIT RELEASED
                </Btn>
              </div>
            ) : null}
          </Panel>
        ))}
        {list.length === 0 && !creating ? (
          <Panel className="p-8">
            <EmptyState title={cs.releases.empty} />
          </Panel>
        ) : null}
      </div>
    </div>
  );
}
